import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styles from './auth/AuthPages.module.css';
import authStyles from '../features/auth/AuthGate.module.css';
import { brandConfig } from '../config/brands';
import { useAuthBrandingSync, useAuthLogo, useAuthScheme } from '../hooks/useAuthScheme';
import { GoogleSignInButton } from '../features/auth/GoogleSignInButton';
import { setStoredToken } from '../services/apiClient';
import type { GoogleSignInResult } from '../features/auth/googleSignIn';
import { appPath, termsHref } from '../utils/navigation';
import { verifyInvitation } from '../features/invitations/api';

export function AcceptInvitationPage() {
  // 他の認証前ページ (SignupSelectPage 等) と同じライトモード切替。
  // authStyles.light は emailLoginButton / divider の明色上書き用。
  useAuthBrandingSync();
  const isLight = useAuthScheme() === 'light';
  const logo = useAuthLogo();
  const wrapperClass = `${styles.wrapper}${isLight ? ` ${styles.light} ${authStyles.light}` : ''}`;
  const { token } = useParams<{ token: string }>();
  const [status, setStatus] = useState<
    'verifying' | 'valid' | 'invalid' | 'expired' | 'resent' | 'already_joined' |
    'belongs_to_other_org'
  >('verifying');
  const [email, setEmail] = useState<string>('');
  const [organizationName, setOrganizationName] = useState<string>('');
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      return;
    }

    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const { ok, data } = await verifyInvitation(token!);

      if (ok && data.valid) {
        setStatus('valid');
        setEmail(data.email!);
        setOrganizationName(data.organization_name || '組織');
      } else if (data.code === 'INVITATION_RESENT') {
        // 招待メールが再送された
        setStatus('resent');
        setEmail(data.email || '');
      } else if (data.code === 'ALREADY_JOINED') {
        // 既に参加済み
        setStatus('already_joined');
        setError(data.error || 'この招待は既に使用済みです');
      } else if (data.code === 'BELONGS_TO_OTHER_ORG') {
        // 同一パートナー内の別組織に既に所属しているため招待を受け入れられない
        setStatus('belongs_to_other_org');
        setError(data.error || 'このメールアドレスは別組織に登録されています');
      } else if (data.error === 'Invitation has expired') {
        setStatus('expired');
      } else {
        setStatus('invalid');
        setError(data.error || '無効な招待リンクです');
      }
    } catch (err) {
      console.error('Token verification failed:', err);
      setStatus('invalid');
      setError('招待の検証に失敗しました');
    }
  };

  const handleGoogleSuccess = useCallback(
    (result: GoogleSignInResult) => {
      setStoredToken(result.token);
      if (result.redirect_to) {
        window.location.assign(appPath(result.redirect_to));
      } else if (result.is_new_user) {
        window.location.assign(appPath('/onboarding'));
      } else {
        window.location.assign(appPath('/'));
      }
    },
    [],
  );

  const handleEmailAuth = () => {
    if (!token) return;
    window.location.assign(appPath(`/auth/email/signup?invitation_token=${token}`));
  };

  if (status === 'verifying') {
    return (
      <div className={wrapperClass}>
        <div className={styles.container}>
          <div className={styles.logoContainer}>
            <img src={logo.src} alt={brandConfig.serviceName} className={styles.logo} />
          </div>
          <div className={styles.card}>
            <h1 className={styles.title}>招待を確認中...</h1>
            <div className={authStyles.loadingSpinner} style={{ margin: '24px auto' }}></div>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'invalid') {
    return (
      <div className={wrapperClass}>
        <div className={styles.container}>
          <div className={styles.logoContainer}>
            <img src={logo.src} alt={brandConfig.serviceName} className={styles.logo} />
          </div>
          <div className={styles.card}>
            <h1 className={styles.title}>無効な招待リンク</h1>
            <p className={styles.subtitle}>
              {error || 'この招待リンクは無効です。管理者に連絡して、新しい招待を依頼してください。'}
            </p>
            <a href={appPath('/')} className={styles.backButton}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6" />
              </svg>
              ホームに戻る
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'expired') {
    return (
      <div className={wrapperClass}>
        <div className={styles.container}>
          <div className={styles.logoContainer}>
            <img src={logo.src} alt={brandConfig.serviceName} className={styles.logo} />
          </div>
          <div className={styles.card}>
            <h1 className={styles.title}>招待の有効期限切れ</h1>
            <p className={styles.subtitle}>
              この招待リンクの有効期限が切れています。管理者に連絡して、新しい招待を依頼してください。
            </p>
            <a href={appPath('/')} className={styles.backButton}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6" />
              </svg>
              ホームに戻る
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'resent') {
    return (
      <div className={wrapperClass}>
        <div className={styles.container}>
          <div className={styles.logoContainer}>
            <img src={logo.src} alt={brandConfig.serviceName} className={styles.logo} />
          </div>
          <div className={styles.card}>
            <h1 className={styles.title}>招待メールを再送信しました</h1>
            <p className={styles.subtitle}>
              {email ? (
                <>
                  <strong>{email}</strong> 宛に新しい招待メールを送信しました。
                  <br />
                </>
              ) : null}
              メールボックスをご確認いただき、新しいリンクからアクセスしてください。
            </p>
            <a href={appPath('/')} className={styles.backButton}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6" />
              </svg>
              ホームに戻る
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'belongs_to_other_org') {
    return (
      <div className={wrapperClass}>
        <div className={styles.container}>
          <div className={styles.logoContainer}>
            <img src={logo.src} alt={brandConfig.serviceName} className={styles.logo} />
          </div>
          <div className={styles.card}>
            <h1 className={styles.title}>この招待は受け入れられません</h1>
            <p className={styles.subtitle}>
              {error || 'このメールアドレスは既に別の組織に登録されているため、この招待を受け入れることができません。'}
            </p>
            <a href={appPath('/')} className={styles.backButton}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6" />
              </svg>
              ホームに戻る
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'already_joined') {
    return (
      <div className={wrapperClass}>
        <div className={styles.container}>
          <div className={styles.logoContainer}>
            <img src={logo.src} alt={brandConfig.serviceName} className={styles.logo} />
          </div>
          <div className={styles.card}>
            <h1 className={styles.title}>既に参加済みです</h1>
            <p className={styles.subtitle}>
              {error || 'この招待は既に使用済みで、組織への参加が完了しています。'}
              <br />
              ログインしてサービスをご利用ください。
            </p>
            <a href={appPath('/')} className={styles.backButton}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6" />
              </svg>
              ログインする
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
      <div className={styles.container}>
        <div className={styles.logoContainer}>
          <img src={logo.src} alt={brandConfig.serviceName} className={styles.logo} />
        </div>

        <div className={styles.card}>
          <h1 className={styles.title}>{organizationName} への招待</h1>
          <p className={styles.subtitle}>
            <strong>{email}</strong> 宛の招待が届いています。
            <br />
            登録方法を選択してください。
          </p>

          {error && <div className={styles.error}>{error}</div>}

          <GoogleSignInButton
            partnerCode={brandConfig.id}
            invitationToken={token}
            mode="signup"
            onSuccess={handleGoogleSuccess}
            onError={(err) => setError(err.message || 'Google認証に失敗しました')}
          />

          <div className={authStyles.divider}>
            <span>または</span>
          </div>

          <button
            type="button"
            className={authStyles.emailLoginButton}
            onClick={handleEmailAuth}
          >
            <svg className={authStyles.googleIcon} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="4" width="20" height="16" rx="2"/>
              <path d="M22 6L12 13L2 6"/>
            </svg>
            <span>メールアドレスで続ける</span>
          </button>
        </div>

        <footer className={styles.footer}>
          <div className={styles.footerLinks}>
            <a href={brandConfig.urls.privacy} target="_blank" rel="noopener noreferrer">プライバシーポリシー</a>
            <span className={styles.footerDivider}>|</span>
            <a href={termsHref()} target="_blank" rel="noopener noreferrer">利用規約</a>
          </div>
        </footer>
      </div>
    </div>
  );
}
